import { Pencil } from 'lucide-react';
import { Link } from 'react-router';
import type { Account, PaginatedResponse } from '@/api/commercial/accounts';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatLgpdCnpj, formatLgpdCpf } from '@/lib/format-lgpd';

interface AccountTableProps {
  data: PaginatedResponse<Account>;
  currentPage: number;
  onPageChange: (page: number) => void;
  canAccess?: boolean;
}

export function AccountTable({
  data,
  currentPage,
  onPageChange,
  canAccess = true,
}: AccountTableProps) {
  // For now, show full access (can be connected to auth context later)
  const permissions = { fullLgpdAccess: true };

  const getClientName = (account: Account): string => {
    if (account.document.type === 'cpf') {
      return account.name || '-';
    }
    return account.social_name || account.fantasy_name || '-';
  };

  const formatDocument = (account: Account): string => {
    const value = account.document.value;
    const isCpf = value.length === 11;

    if (isCpf) {
      return formatLgpdCpf(value, permissions);
    } else {
      return formatLgpdCnpj(value, permissions);
    }
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>CPF/CNPJ</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead className="w-[100px]">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  Nenhuma conta encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <span className="font-medium">{getClientName(account)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDocument(account)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {account.setor?.length ? account.setor.join(', ') : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {canAccess && (
                      <Link to={`/commercial/accounts/${account.id}`}>
                        <Button
                          title="Acessar conta"
                          variant="ghost"
                          className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          <Pencil className="size-5" />
                        </Button>
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
